import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { sendOTP, verifyOTP } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Shield, Loader2, RefreshCw } from "lucide-react";
import OTPInput from "@/components/OTPInput";

export default function CustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [step, setStep] = useState("mobile"); // "mobile" | "otp"
  const [mobile, setMobile] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState(null); // backend returns this only for whitelisted demo numbers when ALLOW_DEMO_OTP=true
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Extract referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref);
  }, [location]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // ---- SEND OTP ----
  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    const cleanMobile = mobile.replace(/\D/g, "");
    if (cleanMobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const res = await sendOTP(cleanMobile, referralCode || undefined);
      if (res.data.success) {
        // The backend returns `dev_otp` ONLY for the whitelisted demo numbers
        // (9999999999 / 8888888888 / 7777777777) when ALLOW_DEMO_OTP=true.
        // That whitelist + env flag IS the security boundary, so when the server
        // chooses to surface it we display it prominently — this is required
        // for the Google Play reviewer who cannot receive SMS on the demo number.
        if (res.data.dev_otp) {
          setDemoOtp(res.data.dev_otp);
          setOtp(res.data.dev_otp); // auto-fill the 6 boxes
          toast.success(`Demo OTP: ${res.data.dev_otp}`, { duration: 12000 });
        } else {
          setDemoOtp(null);
          toast.success("OTP sent to your mobile number!");
        }
        setStep("otp");
        setFailedAttempts(0);
        setCountdown(30);
      } else {
        toast.error(res.data.error || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- OTP INPUT HANDLING ----
  // Handled inside the <OTPInput /> component. We just expose change + complete callbacks.
  const handleOtpComplete = (otpStr) => {
    if (otpStr && otpStr.length === 6) {
      setTimeout(() => handleVerifyOTP(otpStr), 200);
    }
  };

  // ---- VERIFY OTP ----
  const handleVerifyOTP = async (otpValue) => {
    const otpStr = otpValue || otp;
    if (otpStr.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }
    const cleanMobile = mobile.replace(/\D/g, "");
    setLoading(true);
    try {
      const res = await verifyOTP(cleanMobile, otpStr, referralCode || undefined);
      if (res.data.success) {
        toast.success("Login successful!");
        login(res.data.token, res.data.user, "customer");
        if (res.data.is_new_user) {
          navigate("/customer/onboarding");
        } else {
          navigate("/customer/dashboard");
        }
      } else {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        toast.error(res.data.error || "Invalid OTP");

        if (newAttempts >= 3 || res.data.error?.includes("Too many")) {
          toast.error("Too many attempts. Please request a new OTP.");
          setStep("mobile");
          setOtp("");
          setFailedAttempts(0);
        } else {
          setOtp("");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- RESEND OTP ----
  const handleResendOTP = () => {
    if (countdown > 0) return;
    handleSendOTP();
  };

  const maskedMobile = mobile ? `XXXXXX${mobile.slice(-4)}` : "";

  return (
    <div className="min-h-screen safe-area-fill flex flex-col relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 50%, #00BCD4 100%)', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)'}}>
      {/* Background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Header */}
      <header className="px-4 py-4 relative z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => step === "otp" ? setStep("mobile") : navigate("/")} className="text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src="/assets/logo-icon-blue.png" alt="" className="h-8 w-8" />
          <span className="font-bold text-white tracking-tight" style={{fontFamily:'Outfit'}}>REBUILT</span>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="max-w-md w-full">
          {step === "mobile" ? (
            /* ---- MOBILE NUMBER SCREEN ---- */
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/20 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <Phone className="h-8 w-8 text-white" strokeWidth={2} />
                </div>
                <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit'}}>Welcome to Rebuilt</h1>
                <p className="text-sm text-[#6B7280] mt-2">Enter your mobile number to get started</p>
              </div>

              <form onSubmit={handleSendOTP} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#6B7280] font-bold mb-2 block">Mobile Number</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] font-semibold text-sm">+91</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={mobile}
                      onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="Enter 10-digit number"
                      className="w-full pl-14 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-lg font-semibold tracking-wider focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none transition-all"
                      data-testid="mobile-input"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#6B7280] font-bold mb-2 block">Referral Code (Optional)</label>
                  <input
                    type="text"
                    value={referralCode}
                    onChange={e => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="e.g. AGTXX1234"
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm font-mono tracking-wider focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none transition-all"
                    data-testid="referral-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || mobile.length !== 10}
                  className="w-full h-14 rounded-xl font-bold text-lg text-white shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 bg-gradient-to-r from-[#1565C0] to-[#00BCD4] hover:from-[#0D47A1] hover:to-[#00ACC1]"
                  data-testid="send-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send OTP
                    </span>
                  )}
                </button>
              </form>

              <p className="text-xs text-[#9CA3AF] text-center mt-6">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          ) : (
            /* ---- OTP ENTRY SCREEN ---- */
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/20 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <Shield className="h-8 w-8 text-white" strokeWidth={2} />
                </div>
                <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit'}}>Verify OTP</h1>
                <p className="text-sm text-[#6B7280] mt-2">
                  OTP sent to <span className="font-semibold text-[#0A2540]">{maskedMobile}</span>
                </p>
              </div>

              {/* OTP Input Boxes — reusable component, WebView-tested */}
              <OTPInput
                length={6}
                value={otp}
                onChange={setOtp}
                onComplete={handleOtpComplete}
                autoFocus
              />

              {/* Demo / reviewer banner — only rendered when the server explicitly
                  returned dev_otp (i.e. mobile is on the demo whitelist AND the
                  backend has ALLOW_DEMO_OTP=true). Lets Google Play reviewers and
                  internal QA log in without needing real SMS delivery. */}
              {demoOtp && (
                <div
                  data-testid="demo-otp-banner"
                  className="mt-5 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-center"
                >
                  <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
                    Demo Mode — Test OTP
                  </p>
                  <p
                    data-testid="demo-otp-code"
                    className="my-1 font-mono font-black text-emerald-700 tracking-[0.5em]"
                    style={{ fontSize: "32px" }}
                  >
                    {demoOtp}
                  </p>
                  <p className="text-[11px] text-emerald-600">
                    Auto-filled. Tap "Verify &amp; Login" to continue.
                  </p>
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={() => handleVerifyOTP()}
                disabled={loading || otp.length !== 6}
                className="w-full h-14 rounded-xl font-bold text-lg text-white shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                ) : (
                  "Verify & Login"
                )}
              </button>

              {/* Failed attempts warning */}
              {failedAttempts > 0 && failedAttempts < 3 && (
                <p className="text-sm text-red-500 text-center mt-3 font-medium">
                  {3 - failedAttempts} attempt(s) remaining
                </p>
              )}

              {/* Resend OTP */}
              <div className="mt-6 text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-[#6B7280]">
                    Resend OTP in <span className="font-bold text-[#1565C0]">{countdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#1565C0] hover:text-[#0D47A1] transition-colors"
                    data-testid="resend-otp-btn"
                  >
                    <RefreshCw className="h-4 w-4" /> Resend OTP
                  </button>
                )}
              </div>

              {/* Change number */}
              <button
                onClick={() => { setStep("mobile"); setOtp(""); setDemoOtp(null); setFailedAttempts(0); }}
                className="w-full text-center text-sm text-[#6B7280] hover:text-[#0A2540] font-medium mt-4 transition-colors"
              >
                Change mobile number
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
